import React, {useMemo, useState} from 'react';
import {Alert, FlexColumn, FlexRow, Modal, RadioGroupHandler, SubHeading, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ExpenseBoolean, ExpenseRow} from '../ExpenseTypes';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';

type ChangeExpenseApprovalModalProps = {
	expenseItems: ExpenseRow[];
	closeModal: () => void;
};

const ChangeExpenseApprovalModal = ({expenseItems, closeModal}: ChangeExpenseApprovalModalProps): React.ReactElement => {
	const {formatMessage} = useIntl();
	const updateableItems = useMemo(() => expenseItems.filter(expenseItem => !expenseItem.locked), [expenseItems]);

	const [newApprovalStatus, setNewApprovalStatus] = useState<string>('');

	const count = expenseItems.length;
	const lockedCount = expenseItems.length - updateableItems.length;
	const showAlert = lockedCount > 0;
	const allowSave = newApprovalStatus && count > lockedCount;

	const saveChanges = () => {
		const onSuccess = () => {
			closeModal();
		};

		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: updateableItems.map(expenseItem => expenseItem.id),
				approved: newApprovalStatus === ExpenseBoolean.TRUE,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.change_approval.title" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'s'}>
					<FlexRow>
						<SubHeading>
							<FormattedMessage id={'expense_management.approval_status'} />
						</SubHeading>
					</FlexRow>
					<FlexRow>
						<Text color={'medium'}>
							<FormattedMessage id={'expense_management.approval_status_long'} />
						</Text>
					</FlexRow>
				</FlexColumn>
				<FlexColumn gap={'xl'}>
					<RadioGroupHandler onValueChange={setNewApprovalStatus} value={newApprovalStatus}>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={ExpenseBoolean.TRUE} />
							<Text>{formatMessage({id: 'expense_management.change_approval_status.option_1'})}</Text>
						</FlexRow>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={ExpenseBoolean.FALSE} />
							<Text>{formatMessage({id: 'expense_management.change_approval_status.option_2'})}</Text>
						</FlexRow>
					</RadioGroupHandler>
					{showAlert && (
						<Alert
							text={formatMessage(
								{id: 'expense_management.change_approval_status.locked_alert'},
								{count, lockedCount}
							)}
							type={Alert.TYPE.INFORMATION}
						/>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={saveChanges} disabled={!allowSave} dataCy={'confirm'}>
					<FormattedMessage id={'expense_management.actions.confirm_changes'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy={'cancel'}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ChangeExpenseApprovalModal;
